import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert,
    Chip,
    Stack,
} from '@mui/material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { CRMEntry } from '../../models/CRMEntry';
import { format } from 'date-fns';

export const ClientEntries: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const [entries, setEntries] = useState<CRMEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchClientEntries = async () => {
            if (!clientId) return;

            try {
                const q = query(
                    collection(db, 'crmEntries'),
                    where('clientId', '==', clientId)
                );
                const querySnapshot = await getDocs(q);

                const clientEntries: CRMEntry[] = [];
                querySnapshot.forEach((doc) => {
                    clientEntries.push({ id: doc.id, ...doc.data() } as CRMEntry);
                });

                setEntries(clientEntries);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching client entries:', err);
                setError('Failed to load client entries');
                setLoading(false);
            }
        };

        fetchClientEntries();
    }, [clientId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            <Typography variant="h4" gutterBottom>
                Client History
            </Typography>

            {entries.length === 0 ? (
                <Alert severity="info">
                    No CRM entries found for this client.
                </Alert>
            ) : (
                <Paper>
                    <List>
                        {entries.map((entry) => (
                            <ListItem
                                key={entry.id}
                                component={RouterLink}
                                to={`/crm/${entry.id}`}
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Typography variant="h6">
                                                {entry.companyName}
                                            </Typography>
                                            <Chip
                                                label={entry.stage}
                                                size="small"
                                                color={
                                                    entry.stage === 'Closed Won'
                                                        ? 'success'
                                                        : entry.stage === 'Closed Lost'
                                                            ? 'error'
                                                            : 'default'
                                                }
                                            />
                                            {entry.value && (
                                                <Chip
                                                    label={`$${entry.value.toLocaleString()}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            )}
                                        </Stack>
                                    }
                                    secondary={
                                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Created: {format(entry.createdAt.toDate(), 'MMM d, yyyy')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Last Activity: {format(entry.lastActivityAt.toDate(), 'MMM d, yyyy')}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
}; 